// Country examples for /pricing-changes-2026: a typical order and three monthly sales levels in
// local currency, today vs from 1 November 2026. Labelled "examples" on the page.
//
// Sources:
// - avgOrder: average order value per country from our order data (2026-10).
// - fxPerGbp: conversion rates per £1 used by the pricing model (2026-10). Sales levels are set in
//   GBP (£500 / £5,000 / £20,000 a month) and converted with these.
// - cardFee: MY, PH and MX use the flat processing-fee estimates Photohawk deducts there
//   (photohawk-serverless orders.js: 2.7% + RM1 / ₱10 / MX$5). Everywhere else it's the standard
//   card rate from our fees-by-country table (src/components/TransactionFeesInfo.tsx).
// - monthly.given: all-in results from the GBP pricing model (plan fee + commission + card fees, on
//   the cheapest plan at that level), for UK, MY, PH and MX. The UK figures use Stripe fees as
//   actually recorded, so they run ~0.2 points above the table rate.
//   Countries WITHOUT `given` are COMPUTED on the page by allInShare() in
//   src/lib/pricing/changes2026.ts with the same method and the card fee above (the method
//   reproduces the given MY / PH / MX figures to within 0.1 points; see changes2026.test.ts).

export type CountryId = "uk" | "my" | "ph" | "mx" | "co" | "eur" | "us" | "ca";

export interface ExampleCurrency {
  code: string;
  /** Printed before the amount, e.g. "RM". */
  prefix: string;
  digits: number;
}

export interface CountryExample {
  id: CountryId;
  /** ISO 3166-1 alpha-2 codes that default to this example (from the browser's locale region). */
  regions: string[];
  /** flagcdn.com code for the selector ("eu" for the eurozone). */
  flag: string;
  /** Money is shown the way it's written in the country (e.g. "MX$1,700.50", "COP 2.100.000"),
   *  whatever the page language. */
  moneyLocale: string;
  currency: ExampleCurrency;
  /** Average order value, local currency. */
  avgOrder: number;
  /** Local currency per £1. */
  fxPerGbp: number;
  /** Card / payment fee per order: a share of the order plus a fixed local amount. */
  cardFee: { percent: number; fixed: number };
  /** "managed": Photohawk processes the payment and deducts the fee. "stripe": the workspace's own Stripe account. */
  cardFeeKind: "managed" | "stripe";
  /** The rate as shown in the fees-by-country table. */
  cardFeeLabel: string;
  /** All-in share of sales today → new, at £500 / £5,000 / £20,000 a month (pricing-model results). */
  given?: { today: number; next: number }[];
}

/** Monthly sales levels, GBP. */
export const SALES_LEVELS_GBP = [500, 5000, 20000];

const EUROZONE = ["PT", "ES", "IE", "DE", "FR", "IT", "NL", "BE", "AT", "FI", "LU", "GR", "SK", "SI", "CY", "MT", "HR", "EE", "LV", "LT"];

export const COUNTRY_EXAMPLES: CountryExample[] = [
  {
    id: "uk",
    moneyLocale: "en-GB",
    flag: "gb",
    regions: ["GB", "GI"],
    currency: { code: "GBP", prefix: "£", digits: 2 },
    avgOrder: 17.32,
    fxPerGbp: 1,
    cardFee: { percent: 0.015, fixed: 0.2 },
    cardFeeKind: "stripe",
    cardFeeLabel: "1.5% + £0.20",
    given: [
      { today: 0.232, next: 0.189 },
      { today: 0.171, next: 0.156 },
      { today: 0.166, next: 0.151 },
    ],
  },
  {
    id: "my",
    moneyLocale: "en-MY",
    flag: "my",
    regions: ["MY"],
    currency: { code: "MYR", prefix: "RM", digits: 2 },
    avgOrder: 22.51,
    fxPerGbp: 5.414,
    cardFee: { percent: 0.027, fixed: 1 },
    cardFeeKind: "managed",
    cardFeeLabel: "2.7% + RM1.00",
    given: [
      { today: 0.275, next: 0.232 },
      { today: 0.213, next: 0.199 },
      { today: 0.208, next: 0.193 },
    ],
  },
  {
    id: "ph",
    moneyLocale: "en-PH",
    flag: "ph",
    regions: ["PH"],
    currency: { code: "PHP", prefix: "₱", digits: 2 },
    avgOrder: 219.67,
    fxPerGbp: 83.13,
    cardFee: { percent: 0.027, fixed: 10 },
    cardFeeKind: "managed",
    cardFeeLabel: "2.7% + ₱10.00",
    given: [
      { today: 0.276, next: 0.233 },
      { today: 0.215, next: 0.201 },
      { today: 0.21, next: 0.195 },
    ],
  },
  {
    id: "mx",
    moneyLocale: "es-MX",
    flag: "mx",
    regions: ["MX"],
    currency: { code: "MXN", prefix: "MX$", digits: 2 },
    avgOrder: 173.65,
    fxPerGbp: 24.28,
    cardFee: { percent: 0.027, fixed: 5 },
    cardFeeKind: "managed",
    cardFeeLabel: "2.7% + MX$5.00",
    given: [
      { today: 0.259, next: 0.217 },
      { today: 0.198, next: 0.184 },
      { today: 0.193, next: 0.178 },
    ],
  },
  {
    // COMPUTED monthly figures. Colombia is a country where Photohawk processes the payment;
    // the fee is the rate in our fees-by-country table.
    id: "co",
    moneyLocale: "es-CO",
    flag: "co",
    regions: ["CO"],
    currency: { code: "COP", prefix: "COP ", digits: 0 },
    avgOrder: 74598,
    fxPerGbp: 4212,
    cardFee: { percent: 0.034, fixed: 900 },
    cardFeeKind: "managed",
    cardFeeLabel: "3.4% + COP 900",
  },
  {
    // COMPUTED monthly figures. Portugal and the rest of the eurozone: EEA cards on Stripe.
    id: "eur",
    moneyLocale: "pt-PT",
    flag: "eu",
    regions: EUROZONE,
    currency: { code: "EUR", prefix: "€", digits: 2 },
    avgOrder: 13.61,
    fxPerGbp: 1.184,
    cardFee: { percent: 0.015, fixed: 0.25 },
    cardFeeKind: "stripe",
    cardFeeLabel: "1.5% + €0.25",
  },
  {
    // COMPUTED monthly figures.
    id: "us",
    moneyLocale: "en-US",
    flag: "us",
    regions: ["US"],
    currency: { code: "USD", prefix: "US$", digits: 2 },
    avgOrder: 21.18,
    fxPerGbp: 1.3245,
    cardFee: { percent: 0.029, fixed: 0.3 },
    cardFeeKind: "stripe",
    cardFeeLabel: "2.9% + US$0.30",
  },
  {
    // COMPUTED monthly figures.
    id: "ca",
    moneyLocale: "en-CA",
    flag: "ca",
    regions: ["CA"],
    currency: { code: "CAD", prefix: "C$", digits: 2 },
    avgOrder: 11.71,
    fxPerGbp: 1.886,
    cardFee: { percent: 0.029, fixed: 0.3 },
    cardFeeKind: "stripe",
    cardFeeLabel: "2.9% + C$0.30",
  },
];

/** US$ → GBP for plan fees (plans are billed in US$): £1 = US$1.3245. */
export const USD_PER_GBP = 1.3245;

export const DEFAULT_COUNTRY: CountryId = "uk";
